import {
  Archive,
  ArrowCounterClockwise,
  ArrowsOutCardinal,
  Check,
  Copy,
  DotsThree,
  DotsThreeVertical,
  FolderMinus,
  FolderPlus,
  PencilSimple,
  Trash,
} from "@phosphor-icons/react"

import { IconButton } from "~/components/design-system/components"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "~/components/reusable/DropdownMenu"
import { cn } from "~/utils/helpers"
import { ItemWithReason } from "./ItemWithReason"
import { folderDisabledReason } from "./packageDisplay"
import {
  ARCHIVED_BY_FOLDER_REASON,
  isArchivedByFolder,
  pageDisabledReason,
} from "./packagePageGating"
import { cannotNestReason, copyName, topFolderId } from "./packageFolders"
import { usePackageNavigator } from "./packageNavigator"
import type { PackageFolder, RangePackage } from "./types"

/** Where the menu opens from: the page header (⋯) or a rail row (⋮, shown on hover). */
type MenuPlace = "header" | "row"

function MenuTrigger({ place, label }: { place: MenuPlace; label: string }) {
  return (
    <DropdownMenuTrigger requires="edit" allowedTitle={label} asChild>
      <IconButton
        variant="transparent"
        size="sm"
        aria-label={label}
        className={cn(place === "row" && "size-6")}
      >
        {place === "row" ? (
          <DotsThreeVertical size={14} weight="bold" />
        ) : (
          <DotsThree size={16} weight="bold" />
        )}
      </IconButton>
    </DropdownMenuTrigger>
  )
}

function FolderChoice({
  label,
  current,
  title,
  indent,
  disabled,
  reason,
  onSelect,
}: {
  label: string
  current: boolean
  title?: string
  indent?: boolean
  disabled: boolean
  /** Why it can't be picked; shown under the label. */
  reason?: string
  onSelect: () => void
}) {
  return (
    <DropdownMenuItem
      title={title}
      disabled={disabled || current || reason !== undefined}
      onSelect={onSelect}
      className={cn(indent && "pl-6")}
    >
      <span className="min-w-0 flex-1 truncate">
        <ItemWithReason label={label} reason={reason} />
      </span>
      {current && <Check size={14} />}
    </DropdownMenuItem>
  )
}

/** The active folders, each followed by its active subfolders. */
function activeTree(folders: PackageFolder[]) {
  const active = folders.filter((folder) => !folder.deactivated)
  const children = Map.groupBy(active, (folder) => folder.parentId)
  return (children.get(null) ?? []).map((folder) => ({
    folder,
    subfolders: children.get(folder.id) ?? [],
  }))
}

/**
 * Move to ›: in a folder, its own spots first ("Directly in X", its
 * subfolders, New subfolder…), then every other active folder; a top-level
 * package lists every folder under "Into folder".
 */
function PackageMoveTo({ pkg, disabled }: { pkg: RangePackage; disabled: boolean }) {
  const nav = usePackageNavigator()
  const deletingReason = nav.folderDeletingReason
  const tree = activeTree(nav.folders)
  const topId = pkg.folderId === null ? null : topFolderId(nav.folders, pkg.folderId)
  const own = tree.find((entry) => entry.folder.id === topId)
  const others = tree.filter((entry) => entry.folder.id !== topId)
  const move = (folderId: string | null) => nav.movePackage(pkg.id, folderId)
  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger disabled={disabled} className="gap-2">
        <ArrowsOutCardinal size={16} />
        Move to
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="max-h-[60vh] w-64 overflow-y-auto rounded-[12px] border-base bg-base shadow-popover">
        {own && (
          <>
            <FolderChoice
              label={`Directly in ${own.folder.name}`}
              current={pkg.folderId === own.folder.id}
              disabled={nav.busy}
              reason={deletingReason(own.folder.id)}
              onSelect={() => move(own.folder.id)}
            />
            {own.subfolders.map((sub) => (
              <FolderChoice
                key={sub.id}
                label={sub.name}
                indent
                current={pkg.folderId === sub.id}
                disabled={nav.busy}
                reason={deletingReason(sub.id)}
                onSelect={() => move(sub.id)}
              />
            ))}
            <DropdownMenuItem disabled={nav.busy} onSelect={() => nav.moveIntoNewSubfolder(pkg)}>
              <FolderPlus size={16} />
              New subfolder…
            </DropdownMenuItem>
          </>
        )}
        {others.length > 0 && (
          <>
            {own && <DropdownMenuSeparator />}
            <DropdownMenuLabel>{own ? "Other folders" : "Into folder"}</DropdownMenuLabel>
            {others.map(({ folder, subfolders }) => (
              <div key={folder.id}>
                <FolderChoice
                  label={folder.name}
                  current={false}
                  disabled={nav.busy}
                  reason={deletingReason(folder.id)}
                  onSelect={() => move(folder.id)}
                />
                {subfolders.map((sub) => (
                  <FolderChoice
                    key={sub.id}
                    label={sub.name}
                    indent
                    current={false}
                    disabled={nav.busy}
                    reason={deletingReason(sub.id)}
                    onSelect={() => move(sub.id)}
                  />
                ))}
              </div>
            ))}
          </>
        )}
        {pkg.folderId !== null && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem disabled={nav.busy} onSelect={() => move(null)}>
              <FolderMinus size={16} />
              No folder (top level)
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  )
}

/**
 * The package ⋯ menu, the same on the rail row and the page header:
 * Rename…, Move to ›, Duplicate package, Archive or Restore, Delete or Keep.
 */
export function PackageActionsMenu({
  pkg,
  place,
  onRename,
  onDelete,
}: {
  pkg: RangePackage
  place: MenuPlace
  onRename: () => void
  onDelete: () => void
}) {
  const nav = usePackageNavigator()
  const { draft, busy } = nav
  const deleting = draft.isDeleting(pkg.id)
  const disabledReason = pageDisabledReason(pkg, nav.folders, deleting)
  const archivedByFolder = isArchivedByFolder(pkg, nav.folders)
  const duplicate = () => {
    const packageId = crypto.randomUUID()
    nav.stage(
      {
        kind: "duplicatePackage",
        packageId,
        sourceId: pkg.id,
        name: copyName(
          pkg.name,
          nav.packages.map((other) => other.name),
        ),
      },
      { onDone: () => nav.select({ kind: "package", id: packageId }) },
    )
  }

  return (
    <DropdownMenu>
      <MenuTrigger place={place} label={`More actions for ${pkg.name}`} />
      <DropdownMenuContent align="end" onClick={(event) => event.stopPropagation()}>
        <DropdownMenuItem disabled={disabledReason !== undefined} onSelect={onRename}>
          <PencilSimple size={16} />
          <ItemWithReason label="Rename…" reason={disabledReason} />
        </DropdownMenuItem>
        <PackageMoveTo pkg={pkg} disabled={busy || disabledReason !== undefined} />
        <DropdownMenuItem disabled={busy} onSelect={duplicate}>
          <Copy size={16} />
          Duplicate package
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {pkg.ownDeactivated ? (
          <DropdownMenuItem
            disabled={busy}
            onSelect={() =>
              nav.stage({ kind: "setPackageActive", packageId: pkg.id, active: true })
            }
          >
            <ArrowCounterClockwise size={16} />
            Restore package
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem
            disabled={busy || archivedByFolder}
            onSelect={() =>
              nav.stage({ kind: "setPackageActive", packageId: pkg.id, active: false })
            }
          >
            <Archive size={16} />
            <ItemWithReason
              label="Archive package"
              reason={archivedByFolder ? ARCHIVED_BY_FOLDER_REASON : undefined}
            />
          </DropdownMenuItem>
        )}
        {deleting ? (
          <DropdownMenuItem
            disabled={busy}
            onSelect={() => draft.unmarkForDeletion({ packageId: pkg.id })}
          >
            <ArrowCounterClockwise size={16} />
            Keep package
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem disabled={busy} onSelect={onDelete} className="text-fg-negative-text">
            <Trash size={16} />
            Delete package…
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function FolderMoveTo({ folder, disabled }: { folder: PackageFolder; disabled: boolean }) {
  const nav = usePackageNavigator()
  const targets = activeTree(nav.folders).filter(
    (entry) => cannotNestReason(nav.folders, folder.id, entry.folder.id) === undefined,
  )
  const hasSubfolders = nav.folders.some((other) => other.parentId === folder.id)
  const reason = hasSubfolders
    ? `${folder.name} holds subfolders, so it can't become a subfolder itself.`
    : undefined
  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger
        disabled={disabled || reason !== undefined || targets.length === 0}
        className="gap-2"
      >
        <ArrowsOutCardinal size={16} />
        <ItemWithReason label="Move to" reason={reason} />
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="max-h-[60vh] w-64 overflow-y-auto rounded-[12px] border-base bg-base shadow-popover">
        <DropdownMenuLabel>Into folder</DropdownMenuLabel>
        {targets.map(({ folder: target }) => (
          <FolderChoice
            key={target.id}
            label={target.name}
            title={`Move into ${target.name} as a subfolder`}
            current={false}
            disabled={nav.busy}
            reason={nav.folderDeletingReason(target.id)}
            onSelect={() => nav.moveFolder(folder.id, target.id)}
          />
        ))}
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  )
}

/**
 * The folder ⋯ menu, the same on the rail row and the page header. A
 * top-level folder: Rename…, Move to ›, Duplicate folder, New subfolder…,
 * Archive or Restore, Delete or Keep. A subfolder: Rename… and Remove
 * subfolder, which moves its packages up into the folder.
 */
export function FolderActionsMenu({
  folder,
  place,
  onRename,
  onDelete,
}: {
  folder: PackageFolder
  place: MenuPlace
  onRename: () => void
  onDelete: () => void
}) {
  const nav = usePackageNavigator()
  const { draft, busy } = nav
  const deleting = draft.isFolderDeleting(folder.id)
  const disabledReason = folderDisabledReason(folder, deleting)
  const select = (folderId: string) => nav.select({ kind: "folder", id: folderId })
  const duplicate = () => {
    const folderId = crypto.randomUUID()
    nav.stage(
      {
        kind: "duplicateFolder",
        folderId,
        sourceId: folder.id,
        name: copyName(
          folder.name,
          nav.folders.filter((other) => other.parentId === folder.parentId).map((f) => f.name),
        ),
      },
      { onDone: () => select(folderId) },
    )
  }
  const setActive = (active: boolean) =>
    nav.stage({ kind: "setFolderActive", folderId: folder.id, active })
  const rename = (
    <DropdownMenuItem disabled={disabledReason !== undefined} onSelect={onRename}>
      <PencilSimple size={16} />
      <ItemWithReason label="Rename…" reason={disabledReason} />
    </DropdownMenuItem>
  )

  return (
    <DropdownMenu>
      <MenuTrigger place={place} label={`More actions for ${folder.name}`} />
      <DropdownMenuContent align="end" onClick={(event) => event.stopPropagation()}>
        {folder.parentId !== null ? (
          <>
            {rename}
            {folder.ownDeactivated && (
              <DropdownMenuItem disabled={busy} onSelect={() => setActive(true)}>
                <ArrowCounterClockwise size={16} />
                Restore subfolder
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              title="Remove this subfolder; its packages move up into the folder"
              disabled={busy || disabledReason !== undefined}
              onSelect={() => nav.removeSubfolder(folder)}
            >
              <FolderMinus size={16} />
              Remove subfolder
            </DropdownMenuItem>
          </>
        ) : (
          <>
            {rename}
            <FolderMoveTo folder={folder} disabled={busy || disabledReason !== undefined} />
            <DropdownMenuItem disabled={busy} onSelect={duplicate}>
              <Copy size={16} />
              Duplicate folder
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={busy || disabledReason !== undefined}
              onSelect={() => nav.createSubfolder(folder.id)}
            >
              <FolderPlus size={16} />
              New subfolder…
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {folder.ownDeactivated ? (
              <DropdownMenuItem disabled={busy} onSelect={() => setActive(true)}>
                <ArrowCounterClockwise size={16} />
                Restore folder
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem
                disabled={busy || folder.deactivated}
                onSelect={() => setActive(false)}
              >
                <Archive size={16} />
                Archive folder
              </DropdownMenuItem>
            )}
            {deleting ? (
              <DropdownMenuItem
                disabled={busy}
                onSelect={() => draft.unmarkForDeletion({ folderId: folder.id })}
              >
                <ArrowCounterClockwise size={16} />
                Keep folder
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem
                disabled={busy}
                onSelect={onDelete}
                className="text-fg-negative-text"
              >
                <Trash size={16} />
                Delete folder…
              </DropdownMenuItem>
            )}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
